import React from 'react';
import { GetStaticProps, NextPage } from 'next';
import Head from 'next/head';
import { getDestinations } from '../helpers/api';
import Hero from 'day8-react/build/new-components/Hero/Hero';
import Button from 'day8-react/build/new-components/Button/Button';
import { BUTTON_TYPES } from 'day8-react/build/new-components/constants/buttons';
import Card from 'day8-react/build/new-components/Card/Card';
import CardSlider from 'day8-react/build/new-components/CardSlider/CardSlider';
import ComponentVerticalSpacing from 'day8-react/build/new-components/helpers/ComponentVerticalSpacing';
import ComponentWrapper from 'day8-react/build/new-components/helpers/ComponentWrapper';
import * as pricingHelpers from 'day8-react/build/helpers/pricing';

import PageLayout from '../layouts/page';
import Destinations from '../components/Destinations';
import AllHighlights from '../components/AllHighlights/AllHighlights';
import BookingDates from '../components/BookingDates';
import ImageGrid from '../components/DestinationGuide/ImageGrid';
import { AcfHeroType, CTAType, DestinationType, WpPageType } from '../globalTypes';
import wp, { sanitizeDestinationResponse, sanitizeWpResponse } from '../helpers/wp';

type MinPrice = {
  price: number
  currency: string
}
type DestinationGuidePage = {
  bookingdates: WpPageType & {
    acf: {
      bookingdates: []
    }
  }
  page: WpPageType & {
    acf: {
      hero: AcfHeroType,
      gallery: {
        imagelist: {
          ratio: string
          image: {
            id: string
          }
        }[]
      },
      tyw: {
        image1: {}
        image2: {}
        subtitle: string
        title: string
        text: string
        cta: CTAType
      }
    }
  }
  prices: MinPrice[]
  destinations: DestinationType[]
}
const DestinationGuide: NextPage<DestinationGuidePage> = ({
  page,
  destinations,
  bookingdates,
  prices
}) => {
  const minPriceFromPrices = prices?.length > 0 && prices.reduce((prev, curr) => prev.price < curr.price ? prev : curr, { price: Infinity, currency: 'EUR' }) as MinPrice;
  const minPrice = minPriceFromPrices && minPriceFromPrices.price && pricingHelpers.formatCurrency(minPriceFromPrices.price / 100, minPriceFromPrices.currency);
  const {
    hero,
    gallery,
    tyw
  } = page.acf;
  return (
    <PageLayout page={page}>
      <Head>
        <title>Destination guide</title>
      </Head>
      {hero && (
        <Hero
          title={hero.title}
          subtitle={hero.subtitle}
          bgImage={hero.background}
          video={hero.video}
          poster={hero.poster}
          template={hero.video ? 'Video' : 'Basic'}
          cta={hero.button}
          mobileVideo={hero.mobile_video}
          mobilePoster={hero.mobile_poster}
          mobileBgImage={hero.mobile_background}
          overlay
          whiteFont
          centeredDesktop
        />
      )}
      <div className="d8-u-white-bg padding-y-md d8-c-card padding-x-0">
        <div className="max-width-lg container">
          <div className="sticky-navbar flex items-center justify-between">
            <div className="sticky-navbar__left">
              <span className="text-xs d8-u-color-contrast-high d8-u-text-uppercase d8-u-ls-sm">
                {destinations.length} destinations available
              </span>
              {!!minPrice && (
                <p className="text-md d8-u-text-bold" style={{ color: '#013E76' }}>from {minPrice}</p>
              )}
            </div>
            <div className="sticky-navbar__right">
              <Button text="See dates" link="/book" />
            </div>
          </div>
        </div>
      </div>
      <div id="gallery" />
      {gallery && (
        <ComponentVerticalSpacing>
          <ComponentWrapper>
            <CardSlider>
              {gallery.imagelist.map(item => (
                <div key={JSON.stringify(item)} className="flex flex-center margin-x-md">
                  <Card
                    template="ImageCard"
                    size={item.ratio}
                    bgImage={item.image}
                    key={item.image.id}
                  />
                </div>
              ))}
            </CardSlider>
          </ComponentWrapper>
        </ComponentVerticalSpacing>
      )}
      {destinations && destinations.length > 0 && (
        <React.Fragment>
          <Destinations
            title={`${destinations.length || '3'} Destinations`}
            text="We picked our favourite mountain on each continent. Come ski the world with us!"
            destinations={destinations}
            bgImage="https://assets.theskiweek.com/wp-content/uploads/2020/06/tsw-2.jpg"
          />
          <AllHighlights destinations={destinations} />
        </React.Fragment>
      )}
      {bookingdates && bookingdates.acf && (
        <BookingDates
          bookingdates={bookingdates.acf.bookingdates}
        />
      )}
      {tyw && (
        <ComponentVerticalSpacing noBottom>
          <ComponentWrapper>
            <div className="max-width-lg margin-auto">
              <div className="grid grid-gap-lg">
                <div className="col-7@sm">
                  <ImageGrid image1={page.acf.tyw.image1} image2={page.acf.tyw.image2} />
                </div>
                <div className="col-5@sm flex items-center">
                  <div className="d8-u-component-padding text-component">
                    <p className="d8-u-color-contrast-medium text-sm d8-u-ls-sm d8-u-text-bold">{page.acf.tyw.subtitle}</p>
                    <h2>{page.acf.tyw.title}</h2>
                    <p>
                      {page.acf.tyw.text}
                    </p>
                    <Button
                      text={page.acf.tyw.cta.text}
                      buttonType={BUTTON_TYPES.PRIMARY}
                      link={page.acf.tyw.cta.url}
                      external
                    />
                  </div>
                </div>
              </div>
            </div>
          </ComponentWrapper>
        </ComponentVerticalSpacing>
      )}
    </PageLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  try {
    const pages: WpPageType[] = await wp.pages().slug('destination-guide').get();
    const bookingdates = await wp.pages().slug('booking-dates').get();
    const destinations: DestinationType[] = await getDestinations();
    const prices = destinations.filter(d => !!d.fromPrice).map(d => ({
      price: d.fromPrice ? d.fromPrice * 100 : null,
      currency: d.currency
    }));
    return {
      props: {
        page: sanitizeWpResponse(pages[0]),
        bookingdates,
        destinations: destinations.map((d: DestinationType) => sanitizeDestinationResponse(d, true)),
        prices
      },
      revalidate: 300
    };
  } catch (e) {
    return { notFound: true };
  }
};

export default DestinationGuide;